/**
 * LeafletMarketMapEditor - Map Editor Component using Leaflet
 * Component cho việc chỉnh sửa Market Map trong admin
 *
 * Features:
 * - Display markers with custom colors
 * - Draggable center marker
 * - Draggable temp marker for adding new markers
 * - Click to select marker
 * - Map style switching
 */
import { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { TILE_LAYERS, GOOGLE_STYLE_MAP } from './LeafletMap'

// Default center: Da Nang
const DEFAULT_CENTER = [16.0544, 108.2022]
const DEFAULT_ZOOM = 13

// Create circle marker icon
function createCircleIcon(color, size = 16, label = '') {
  const hasLabel = label && label.length > 0
  const width = hasLabel ? Math.max(size * 2, label.length * 8 + 16) : size
  const height = size

  return L.divIcon({
    className: 'custom-marker-wrapper',
    html: `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: ${width}px;
        height: ${height}px;
        padding: 0 8px;
        background: ${color};
        border: 2px solid white;
        border-radius: ${hasLabel ? '12px' : '50%'};
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        color: white;
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
      ">${label || ''}</div>
    `,
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
    popupAnchor: [0, -height / 2],
  })
}

// Create center marker icon (draggable blue marker)
function createCenterIcon() {
  return L.divIcon({
    className: 'center-marker-wrapper',
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background: #2563eb;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 14px;
        font-weight: bold;
        cursor: move;
      ">⊕</div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  })
}

// Create temp marker icon (for adding new markers)
function createTempIcon() {
  return L.divIcon({
    className: 'temp-marker-wrapper',
    html: `
      <div style="
        width: 28px;
        height: 28px;
        background: #10B981;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 18px;
        font-weight: bold;
        cursor: move;
        animation: bounce 0.5s ease;
      ">+</div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  })
}

// Utility marker icon map (emoji-based for simplicity)
const UTILITY_ICONS = {
  default: '📍',
  school: '🏫',
  hospital: '🏥',
  bus_station: '🚌',
  police: '👮',
  government: '🏛️',
  bank: '🏦',
  market: '🏪',
  supermarket: '🛒',
  restaurant: '🍽️',
  cafe: '☕',
  hotel: '🏨',
  gas_station: '⛽',
  parking: '🅿️',
  pharmacy: '💊',
  church: '⛪',
  temple: '🛕',
  mosque: '🕌',
  park: '🌳',
  gym: '🏋️',
  stadium: '🏟️',
  airport: '✈️',
  train_station: '🚉',
  port: '⚓',
  bridge: '🌉',
  tower: '🗼',
  monument: '🗿',
  beach: '🏖️',
  mountain: '⛰️',
  mall: '🏬',
  cinema: '🎬',
  atm: '💳',
  post_office: '📮',
  fire_station: '🚒',
  university: '🎓',
  kindergarten: '👶',
  library: '📚',
}

// Create utility marker icon
function createUtilityIcon(iconType = 'default', name = '') {
  const emoji = UTILITY_ICONS[iconType] || UTILITY_ICONS.default
  const showName = name // Show full name, user controls length manually

  return L.divIcon({
    className: 'utility-marker-wrapper',
    html: `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        transform: translateY(-10px);
      ">
        <div style="
          width: 32px;
          height: 32px;
          background: white;
          border: 2px solid #6B7280;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
        ">${emoji}</div>
        ${showName ? `<div style="
          margin-top: 2px;
          padding: 1px 6px;
          background: rgba(255,255,255,0.95);
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          color: #374151;
          white-space: nowrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        ">${showName}</div>` : ''}
      </div>
    `,
    iconSize: [32, 44],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })
}

const LeafletMarketMapEditor = forwardRef(function LeafletMarketMapEditor(
  {
    markers = [],
    utilityMarkers = [],
    center = { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] },
    zoom = DEFAULT_ZOOM,
    mapStyle = 'hybrid',
    showCenterMarker = true,
    onCenterChange,
    onZoomChange,
    onMarkerClick,
    onUtilityMarkerClick,
    onTempMarkerConfirm,
    onMoveEnd, // Called when map stops moving
    addingMarker = false,
    distanceLine = null, // [[lat1, lng1], [lat2, lng2]] for distance line
  },
  ref
) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersLayerRef = useRef(null)
  const utilityMarkersLayerRef = useRef(null)
  const centerMarkerRef = useRef(null)
  const tempMarkerRef = useRef(null)
  const distanceLineRef = useRef(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const initialViewSetRef = useRef(false) // Track if initial view has been set

  // Convert center format
  const centerLatLng = Array.isArray(center)
    ? center
    : [center.lat || DEFAULT_CENTER[0], center.lng || DEFAULT_CENTER[1]]

  // Convert Google map style to Leaflet tile layer
  const tileLayerKey = GOOGLE_STYLE_MAP[mapStyle] || 'satellite'

  // Expose map methods via ref
  useImperativeHandle(ref, () => ({
    getMap: () => mapRef.current,
    panTo: (lat, lng, targetZoom) => {
      if (mapRef.current) {
        mapRef.current.flyTo([lat, lng], targetZoom || mapRef.current.getZoom(), {
          duration: 0.5,
        })
      }
    },
    setZoom: (targetZoom) => {
      if (mapRef.current) {
        mapRef.current.setZoom(targetZoom)
      }
    },
    getCenter: () => {
      if (mapRef.current) {
        const c = mapRef.current.getCenter()
        return { lat: c.lat, lng: c.lng }
      }
      return null
    },
    getZoom: () => {
      if (mapRef.current) {
        return mapRef.current.getZoom()
      }
      return null
    },
    setMapStyle: (style) => {
      if (!mapRef.current) return
      const map = mapRef.current
      const layerKey = GOOGLE_STYLE_MAP[style] || 'satellite'
      const tileConfig = TILE_LAYERS[layerKey] || TILE_LAYERS.satellite

      // Remove existing tile layers
      map.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          map.removeLayer(layer)
        }
      })

      // Add new tile layer (below markers)
      L.tileLayer(tileConfig.url, {
        attribution: tileConfig.attribution,
        maxZoom: 19,
      }).addTo(map)

      // Re-add markers layer on top
      if (markersLayerRef.current) {
        markersLayerRef.current.addTo(map)
      }
    },
    // Create temp marker for drag mode
    createTempMarker: () => {
      if (!mapRef.current || tempMarkerRef.current) return

      const map = mapRef.current
      const mapCenter = map.getCenter()

      const tempMarker = L.marker([mapCenter.lat, mapCenter.lng], {
        icon: createTempIcon(),
        draggable: true,
        zIndexOffset: 10000,
      }).addTo(map)

      tempMarkerRef.current = tempMarker

      // Click on temp marker to confirm position
      tempMarker.on('click', () => {
        const pos = tempMarker.getLatLng()
        if (onTempMarkerConfirm) {
          onTempMarkerConfirm(pos.lat, pos.lng)
        }
      })

      return tempMarker
    },
    // Remove temp marker
    removeTempMarker: () => {
      if (tempMarkerRef.current) {
        tempMarkerRef.current.remove()
        tempMarkerRef.current = null
      }
    },
    // Update temp marker position (for coordinate input mode)
    updateTempMarkerPosition: (lat, lng) => {
      if (tempMarkerRef.current) {
        tempMarkerRef.current.setLatLng([lat, lng])
      } else if (mapRef.current) {
        // Create temp marker if not exists
        const tempMarker = L.marker([lat, lng], {
          icon: createTempIcon(),
          draggable: true,
          zIndexOffset: 10000,
        }).addTo(mapRef.current)

        tempMarkerRef.current = tempMarker

        // Click on temp marker to confirm position
        tempMarker.on('click', () => {
          const pos = tempMarker.getLatLng()
          if (onTempMarkerConfirm) {
            onTempMarkerConfirm(pos.lat, pos.lng)
          }
        })
      }
    },
    // Update center marker position
    setCenterMarkerPosition: (lat, lng) => {
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setLatLng([lat, lng])
      }
    },
  }))

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    const map = L.map(containerRef.current, {
      center: centerLatLng,
      zoom: zoom,
      zoomControl: true,
      attributionControl: true,
    })

    mapRef.current = map

    // Add tile layer
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.satellite
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Create markers layer group
    markersLayerRef.current = L.layerGroup().addTo(map)

    // Create utility markers layer group
    utilityMarkersLayerRef.current = L.layerGroup().addTo(map)

    // Create center marker (draggable)
    const centerMarker = L.marker(centerLatLng, {
      icon: createCenterIcon(),
      draggable: true,
      zIndexOffset: 9999,
    }).addTo(map)

    centerMarkerRef.current = centerMarker

    // Center marker drag end
    centerMarker.on('dragend', () => {
      const pos = centerMarker.getLatLng()
      if (onCenterChange) {
        onCenterChange(pos.lat, pos.lng)
      }
      // Pan map to new center
      map.panTo([pos.lat, pos.lng])
    })

    // Zoom change handler
    map.on('zoomend', () => {
      if (onZoomChange) {
        onZoomChange(map.getZoom())
      }
    })

    // Move end handler - for mobile location picking
    map.on('moveend', () => {
      if (onMoveEnd) {
        const c = map.getCenter()
        onMoveEnd({ lat: c.lat, lng: c.lng })
      }
    })

    setMapLoaded(true)

    // Cleanup
    return () => {
      if (tempMarkerRef.current) {
        tempMarkerRef.current.remove()
        tempMarkerRef.current = null
      }
      if (centerMarkerRef.current) {
        centerMarkerRef.current.remove()
        centerMarkerRef.current = null
      }
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Update tile layer when mapStyle changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current
    const tileConfig = TILE_LAYERS[tileLayerKey] || TILE_LAYERS.satellite

    // Remove existing tile layers
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer)
      }
    })

    // Add new tile layer (below markers)
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Re-add markers layer on top
    if (markersLayerRef.current) {
      markersLayerRef.current.addTo(map)
    }
    // Re-add center marker on top
    if (centerMarkerRef.current) {
      centerMarkerRef.current.addTo(map)
    }
  }, [tileLayerKey, mapLoaded])

  // Toggle center marker visibility
  useEffect(() => {
    if (!centerMarkerRef.current || !mapRef.current) return

    if (showCenterMarker) {
      if (!mapRef.current.hasLayer(centerMarkerRef.current)) {
        centerMarkerRef.current.addTo(mapRef.current)
      }
    } else {
      if (mapRef.current.hasLayer(centerMarkerRef.current)) {
        centerMarkerRef.current.remove()
      }
    }
  }, [showCenterMarker, mapLoaded])

  // Update markers when data changes
  useEffect(() => {
    if (!mapLoaded || !markersLayerRef.current) return

    const markersLayer = markersLayerRef.current

    // Clear existing markers
    markersLayer.clearLayers()

    // Create new markers
    markers.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) return

      const lat = parseFloat(markerData.latitude)
      const lng = parseFloat(markerData.longitude)
      const color = markerData.marker_color || '#EF4444'
      const label = markerData.price_text || '?'

      const marker = L.marker([lat, lng], {
        icon: createCircleIcon(color, 16, label),
      })

      // Click handler
      marker.on('click', () => {
        if (onMarkerClick) {
          onMarkerClick(markerData)
        }
      })

      markersLayer.addLayer(marker)
    })
  }, [markers, mapLoaded, onMarkerClick])

  // Update utility markers when data changes
  useEffect(() => {
    if (!mapLoaded || !utilityMarkersLayerRef.current) return

    const utilityLayer = utilityMarkersLayerRef.current

    // Clear existing utility markers
    utilityLayer.clearLayers()

    // Create new utility markers
    utilityMarkers.forEach((markerData) => {
      if (!markerData.latitude || !markerData.longitude) return

      const lat = parseFloat(markerData.latitude)
      const lng = parseFloat(markerData.longitude)
      const iconType = markerData.icon_type || 'default'
      const name = markerData.name || ''

      const marker = L.marker([lat, lng], {
        icon: createUtilityIcon(iconType, name),
      })

      // Click handler
      marker.on('click', () => {
        if (onUtilityMarkerClick) {
          onUtilityMarkerClick(markerData)
        }
      })

      utilityLayer.addLayer(marker)
    })
  }, [utilityMarkers, mapLoaded, onUtilityMarkerClick])

  // Update center marker position when center prop changes
  useEffect(() => {
    if (!centerMarkerRef.current || !mapLoaded) return
    centerMarkerRef.current.setLatLng(centerLatLng)
  }, [centerLatLng[0], centerLatLng[1], mapLoaded])

  // Update map center/zoom when props change (ONLY on initial load)
  // This prevents the map from jumping back to center when user pans
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    // Only set view once when data is first loaded
    // After that, user controls the map view freely
    if (!initialViewSetRef.current) {
      mapRef.current.setView(centerLatLng, zoom, { animate: false })
      initialViewSetRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded]) // Only depend on mapLoaded, not center/zoom to prevent jumping

  // Auto create/remove temp marker when addingMarker changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    if (addingMarker) {
      // Create temp marker if not exists
      if (!tempMarkerRef.current) {
        const map = mapRef.current
        const mapCenter = map.getCenter()

        const tempMarker = L.marker([mapCenter.lat, mapCenter.lng], {
          icon: createTempIcon(),
          draggable: true,
          zIndexOffset: 10000,
        }).addTo(map)

        tempMarkerRef.current = tempMarker

        // Click on temp marker to confirm position
        tempMarker.on('click', () => {
          const pos = tempMarker.getLatLng()
          if (onTempMarkerConfirm) {
            onTempMarkerConfirm(pos.lat, pos.lng)
          }
        })
      }
    } else {
      // Remove temp marker
      if (tempMarkerRef.current) {
        tempMarkerRef.current.remove()
        tempMarkerRef.current = null
      }
    }
  }, [addingMarker, mapLoaded, onTempMarkerConfirm])

  // Update distance line
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return

    const map = mapRef.current

    // Remove existing distance line
    if (distanceLineRef.current) {
      map.removeLayer(distanceLineRef.current)
      distanceLineRef.current = null
    }

    // Add new distance line if provided
    if (distanceLine && distanceLine.length === 2) {
      const polyline = L.polyline(distanceLine, {
        color: '#3B82F6',
        weight: 3,
        opacity: 0.8,
        dashArray: '10, 10',
        className: 'distance-line',
      })

      // Add endpoint markers
      const startMarker = L.circleMarker(distanceLine[0], {
        radius: 6,
        fillColor: '#3B82F6',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 1,
      })

      const endMarker = L.circleMarker(distanceLine[1], {
        radius: 6,
        fillColor: '#10B981',
        color: '#fff',
        weight: 2,
        opacity: 1,
        fillOpacity: 1,
      })

      // Create layer group
      distanceLineRef.current = L.layerGroup([polyline, startMarker, endMarker])
      distanceLineRef.current.addTo(map)

      // Fit bounds to show both points
      map.fitBounds(L.latLngBounds(distanceLine), { padding: [50, 50] })
    }
  }, [distanceLine, mapLoaded])

  return (
    <div className="w-full h-full relative">
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ minHeight: '300px', background: '#E8E0D5' }}
      />

      {/* Loading overlay */}
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500">Đang tải bản đồ...</p>
          </div>
        </div>
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .temp-marker-wrapper > div {
          animation: bounce 0.5s ease;
        }
      `}</style>
    </div>
  )
})

export default LeafletMarketMapEditor
export { UTILITY_ICONS }
